import React, { useEffect, useRef } from 'react'
import { Switch, Route, Redirect, useLocation, Link } from 'react-router-dom'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { LinkIcon, DuplicateIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import Breadcrumbs from '@/Layout/Breadcrumbs'
import PageTitle from '@/Layout/PageTitle'
import Loading from '@/shared/Loading'

import ProfileTabs from '@/pages/Users/ProfileTabs'
import ProfileGeneral from '@/pages/Users/ProfileGeneral'
import ProfileWork from '@/pages/Users/ProfileWork'
import ProfileVisibility from '@/pages/Users/ProfileVisibility'

const TITLES = { '/profile': 'Edit profile', '/profile/work': 'My work', '/profile/visibility': 'Visibility' }

export default function UsersContainer (props) {
  const location = useLocation()
  const isComponentMounted = useRef(false)
  const { getRequest } = useQuery()
  const [, setUser] = useGlobalState('userContainerUser')
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')

  useEffect(() => {
    getRequest(`/api/v3/users/${currentUser.id}/edit`, {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setUser(jsonData.user)
    })
    isComponentMounted.current = true
  }, [])

  if (!isComponentMounted.current) { return <Loading /> }

  const copied = () => setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
    <p className='text-sm font-medium text-green-600 dark:text-green-300'>Copied</p>
    <p className='mt-1 text-sm text-green-500 dark:text-green-300'>You did it! It's copied. You can paste it anywhere you want now.</p>
  </div>)

  const title = TITLES[location.pathname] || 'Edit profile'

  return <>
    <Breadcrumbs>
      <Link to='/profile' className='text-xs text-gray-500 hover:text-gray-700 dark:text-gray-100 sm:text-sm'>Profile</Link>
      { title !== 'Edit profile' && <Link to={location.pathname} className='text-xs text-gray-500 hover:text-gray-700 dark:text-gray-100 sm:text-sm'>{title}</Link> }
    </Breadcrumbs>
    <PageTitle pagetitle={title}>
      <CopyToClipboard key='copyprofile' text={`https://www.castingcall.club/m/${currentUser.username}`} onCopy={copied}>
        <button type='button' title='Copy your public profile link' aria-label='Copy public profile link'
          className='inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'>
          <DuplicateIcon className='h-5 w-5' />
        </button>
      </CopyToClipboard>
      <Link key='profilelink' to={`/m/${currentUser.username}`} className='ml-2 inline-flex items-center gap-x-1 rounded-md bg-cccblue px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cccblue-alt hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900'>
        <LinkIcon className='h-4 w-4' /> View public profile
      </Link>
    </PageTitle>
    <ProfileTabs />
    <div className='mt-4'>
      <Switch>
        <Route {...props} exact path='/profile'><ProfileGeneral /></Route>
        <Route {...props} exact path='/profile/work'><ProfileWork /></Route>
        <Route {...props} exact path='/profile/visibility'><ProfileVisibility /></Route>
        {/* Old tab paths, still linked from emails and the mobile app. */}
        <Redirect from='/profile/general' to='/profile' />
        <Redirect from='/profile/about' to='/profile/work' />
        <Redirect from='/profile/submissions' to='/profile/visibility' />
        <Redirect from='/profile/projects' to='/profile/visibility' />
        <Redirect from='/profile/options' to='/profile/visibility' />
      </Switch>
    </div>
  </>
}
